import { useEffect, useRef } from 'react';
import { usePublicFacility } from '../context/PublicFacilityContext';

// The facility's website field is meant to hold a bare domain, but an
// operator typing "https://www.example.com/" is ordinary (2026-10-20: it
// produced a redirect to "https://https://…"). Reduce whatever is stored to
// the host before building an address from it.
export const websiteHost = (value) =>
  String(value || '')
    .trim()
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/[/?#].*$/, '')
    .toLowerCase();

const SubdomainRedirect = () => {
  const { facility, slug, loading } = usePublicFacility();
  // Only allow the cross-document redirect to fire ONCE per SPA session,
  // and only when the page initially loaded on '/'. Otherwise an in-app
  // back navigation into '/' (e.g. tenant in the rent wizard hits the
  // browser back button enough times to land on the marketing root)
  // would re-fire window.location.replace() mid-React-render and tear
  // down a still-mounted subtree while its in-flight axios calls are
  // resolving -- the "Something went wrong" crash on Rent Now.
  const initialPathRef = useRef(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  );
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (loading || !slug || !facility) return;
    if (initialPathRef.current !== '/') return;
    if (window.location.pathname !== '/') return;
    const target = websiteHost(facility.website_domain);
    // Only redirect to a *different external* site. A mySMS360-native
    // facility (no external website, e.g. a freshly acquired location on
    // {facility}.mysms360.com) must stay on its own subdomain and serve the
    // rent/portal pages directly — never bounce to itself or nowhere.
    if (!target) return;
    if (target === window.location.host.toLowerCase()
        || target === window.location.hostname.toLowerCase()) return;
    firedRef.current = true;
    const qs = window.location.search || '';
    window.location.replace(`https://${target}${qs}`);
  }, [facility, slug, loading]);

  return null;
};

export default SubdomainRedirect;
